import React from "react";
import { jsPDF } from "jspdf";
import logoSrc from "../pages/vistaar.png";
import IconButton from "@mui/material/IconButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { formatPermitTrackingId } from "../utils/permitTracking";
import { restApiUrl, headers } from "../pages/app_props";

async function dataUrlFromImageSrc(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/* Palette pulled from the on-screen light theme (index.css --pd-* tokens) so
   PDF pills/text match the app's actual colors instead of generic Tailwind hues. */
const PD_OK = [31, 138, 76];       // --pd-ok
const PD_WARN = [184, 121, 10];    // --pd-warn
const PD_CRIT = [195, 52, 52];     // --pd-crit
const PD_INFO = [37, 99, 166];     // --pd-info
const PD_MUTED = [92, 111, 107];   // --pd-muted
const PD_LINE = [221, 228, 225];   // --pd-line
const PD_SURFACE_2 = [245, 248, 247]; // --pd-surface-2
const PD_INK = [22, 33, 31];       // --pd-ink
const PD_ACCENT = [13, 110, 92];   // --pd-accent

function statusColor(s) {
  switch (s) {
    case "DRAFT": return PD_MUTED;
    case "SUBMITTED": return PD_INFO;
    case "COMPETENT_PERSON_APPROVAL":
    case "ISSUER_DEPARTMENT_HOD_REVIEW":
    case "CLEARANCE_DEPARTMENT_REVIEW":
    case "PERMIT_TYPE_REVIEW":
    case "APPROVED": return PD_WARN;
    case "ISSUED": return PD_OK;
    case "ACTIVE": return PD_OK;
    case "SUSPENDED": return PD_WARN;
    case "CLOSED": return PD_MUTED;
    case "CANCELLED": return PD_CRIT;
    default: return PD_MUTED;
  }
}
function riskColor(l) {
  if (!l) return PD_INK;
  const u = l.toUpperCase();
  if (u === "HIGH" || u === "CRITICAL" || u === "EXTREME") return PD_CRIT;
  if (u === "MEDIUM" || u === "MODERATE") return PD_WARN;
  if (u === "LOW") return PD_OK;
  return PD_MUTED;
}
function priorityColor(p) {
  if (!p) return PD_INK;
  switch (p.toUpperCase()) {
    case "HIGH": return PD_CRIT;
    case "MEDIUM": return PD_WARN;
    case "LOW": return PD_OK;
    default: return PD_MUTED;
  }
}
function clStatusColor(s) {
  switch (s) {
    case "DONE": return PD_OK;
    case "PENDING": return PD_WARN;
    case "NA": return PD_MUTED;
    default: return PD_MUTED;
  }
}
function jsaColor(s) {
  switch ((s || "").toUpperCase()) {
    case "APPROVED": return PD_OK;
    case "REJECTED": return PD_CRIT;
    case "SUBMITTED": return PD_INFO;
    default: return PD_MUTED;
  }
}
function lighten(rgb, amt) {
  return rgb.map((c) => Math.round(c + (255 - c) * amt));
}

const fmtDt = (v) => v ? v.replace("T", " ").slice(0, 16) : "-";
const fmtNow = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const fmtDate = (v) => v ? String(v).slice(0, 10) : "-";
const str = (v) => (v === null || v === undefined) ? "-" : String(v);

async function buildPermitPdf(permit, extras = {}) {
  const {
    gasTests = [],
    revalidations = [],
    isolationRecords = [],
    coordinationMeetings = [],
    shiftHandovers = [],
    toolBoxTalks = [],
    jsaList = [],
    blanketCheckins = [],
    simopsConflicts = [],
    permitTypeBlocks = [],
    riskItems = [],
    riskAssessment = null,
  } = extras;

  const resolvedBlocks = permitTypeBlocks.length > 0 ? permitTypeBlocks : (permit?.permitTypeBlocks || []);
  const resolvedRiskItems = riskItems.length > 0 ? riskItems
    : Array.isArray(permit?.riskAssessmentItems) ? permit.riskAssessmentItems
      : Array.isArray(permit?.riskAssessment?.riskAssessmentItems) ? permit.riskAssessment.riskAssessmentItems
        : Array.isArray(permit?.riskAssessment?.items) ? permit.riskAssessment.items
          : Array.isArray(permit?.riskAssessment) ? permit.riskAssessment : [];
  const resolvedRA = riskAssessment || permit?.riskAssessment || null;

  /* ── PDF setup ── */
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const PW = pdf.internal.pageSize.getWidth();
  const PH = pdf.internal.pageSize.getHeight();
  const M = 40;          // margin
  const GAP = 16;          // column gap
  const CW = (PW - M * 2 - GAP) / 2;
  const LH = 15;          // line height
  const SECTION_MB = 12;          // space before section bar
  let y = 60;

  const logoData = await dataUrlFromImageSrc(logoSrc);
  const black = () => pdf.setTextColor(0, 0, 0);

  /* ── Soft "wash" pill badge — light tinted background, colored text + dot,
     matching the on-screen .pd-pill style (not a solid color/white text badge). ── */
  const pill = (text, color, x, baselineY, opts = {}) => {
    const fs = opts.fontSize || 8.5;
    pdf.setFontSize(fs);
    pdf.setFont("helvetica", "bold");
    const dotR = 1.6, dotGap = 4;
    const tw = pdf.getTextWidth(text);
    const padX = 7, h = fs + 5.5;
    const w = tw + padX * 2 + dotR * 2 + dotGap;
    const wash = lighten(color, 0.85);
    pdf.setFillColor(...wash);
    pdf.roundedRect(x, baselineY - h + 3.5, w, h, h / 2, h / 2, "F");
    pdf.setFillColor(...color);
    pdf.circle(x + padX + dotR, baselineY - 2.6, dotR, "F");
    pdf.setTextColor(...color);
    pdf.text(text, x + padX + dotR * 2 + dotGap, baselineY);
    black();
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    return w;
  };

  /* ── Page header (title, tracking id, status pill, logo) ── */
  const pageHeader = () => {
    pdf.setFontSize(17);
    pdf.setFont("helvetica", "bold");
    black();
    pdf.text("Permit Details", M, 34);
    let hx = M + pdf.getTextWidth("Permit Details") + 12;
    pdf.setFontSize(9.5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...PD_MUTED);
    const trackId = formatPermitTrackingId(permit);
    pdf.text(trackId, hx, 34);
    hx += pdf.getTextWidth(trackId) + 10;
    black();
    if (permit?.status) {
      pill(permit.status.replace(/_/g, " "), statusColor(permit.status), hx, 37);
    }
    if (logoData) pdf.addImage(logoData, "PNG", PW - 112, 8, 34, 34);
    pdf.setDrawColor(210, 214, 219);
    pdf.setLineWidth(0.6);
    pdf.line(M, 46, PW - M, 46);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.2);
  };

  let pageNum = 1;
  const pageBreak = (need = LH + 4) => {
    if (y + need > PH - 55) { pdf.addPage(); pageNum++; y = 62; pageHeader(); }
  };

  /* ── Generic multi-row summary bar: N cells wrapped into rows of `cols`
     columns, each cell a label/value pair (optionally colored) — mirrors the
     on-screen .pd-summary-strip 8-column layout used for the top permit
     details rows. ── */
  const gridInfoBar = (cells, cols = 8) => {
    const rowsOfCells = [];
    for (let i = 0; i < cells.length; i += cols) rowsOfCells.push(cells.slice(i, i + cols));
    const laneW = (PW - M * 2) / cols;
    const valFontSize = 8.5, valLineH = 10, labelH = 14, padTop = 12, padBottom = 8;

    /* Measure each row's height from the tallest wrapped value in it so long
       values (long tracking IDs, combined "X -> Y" fields, long statuses)
       never get clipped — same measurement is used to draw, so the two
       passes can't drift. */
    pdf.setFontSize(valFontSize); pdf.setFont("helvetica", "bold");
    const measuredRows = rowsOfCells.map((rowCells) => {
      const linesPerCell = rowCells.map(([, val]) => pdf.splitTextToSize(str(val), laneW - 14));
      const maxLines = Math.max(...linesPerCell.map((l) => l.length), 1);
      return { rowCells, linesPerCell, h: labelH + padTop + maxLines * valLineH + padBottom };
    });
    const totalH = measuredRows.reduce((sum, r) => sum + r.h, 0);
    pageBreak(totalH + 16);
    const boxY = y;
    pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
    pdf.roundedRect(M, boxY, PW - M * 2, totalH, 3, 3);
    let ry = boxY;
    measuredRows.forEach(({ rowCells, linesPerCell, h }, ri) => {
      if (ri > 0) {
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
        pdf.line(M, ry, PW - M, ry);
      }
      rowCells.forEach(([label, , color], i) => {
        const cx = M + laneW * i + 8;
        if (i > 0) {
          pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
          pdf.line(M + laneW * i, ry + 6, M + laneW * i, ry + h - 6);
        }
        pdf.setFontSize(7); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_MUTED);
        const labelLines = pdf.splitTextToSize(label, laneW - 14);
        pdf.text(labelLines[0] || "", cx, ry + 12);
        pdf.setFontSize(valFontSize); pdf.setTextColor(...(color || PD_INK)); pdf.setFont("helvetica", "bold");
        const lines = linesPerCell[i];
        (lines.length > 0 ? lines : ["-"]).forEach((l, li) => {
          pdf.text(l, cx, ry + labelH + padTop + li * valLineH);
        });
      });
      ry += h;
    });
    black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    y = boxY + totalH + 16;
  };

  /* ── Top summary bar: ID / Tracking ID / Site / Issuer Dept / Receiver Dept / Priority / Schedule / Actual ── */
  const topInfoBar = () => {
    gridInfoBar([
      ["ID", str(permit?.id)],
      ["TRACKING ID", formatPermitTrackingId(permit)],
      ["SITE", str(permit?.siteName)],
      ["ISSUER DEPT", str(permit?.issuerDepartmentName)],
      ["RECEIVER DEPT", str(permit?.receiverDepartmentName)],
      ["PRIORITY", str(permit?.priority)],
      ["SCHEDULE", `${fmtDt(permit?.scheduleStart)} -> ${fmtDt(permit?.scheduleEnd)}`],
      ["ACTUAL", `${fmtDt(permit?.actualStart)} -> ${fmtDt(permit?.actualEnd)}`],
    ], 8);
  };

  /* ── Section title: light card-style header bar (matches on-screen card headers) ── */
  const section = (title) => {
    y += SECTION_MB;
    pageBreak(30);
    pdf.setFillColor(...PD_SURFACE_2);
    pdf.setDrawColor(...PD_LINE);
    pdf.setLineWidth(0.6);
    pdf.roundedRect(M, y, PW - M * 2, 22, 2, 2, "FD");
    pdf.setTextColor(...PD_MUTED);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.text(title.toUpperCase(), M + 9, y + 15);
    black();
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.2);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    y += 22 + 10;
  };

  /* ── Sub-header (light card row, used for cycles / groupings) ── */
  const subHead = (title) => {
    y += 6;
    pageBreak(22);
    pdf.setFillColor(...PD_SURFACE_2);
    pdf.setDrawColor(...PD_LINE);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(M, y, PW - M * 2, 17, 2, 2, "FD");
    pdf.setFontSize(9.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PD_INK);
    pdf.text(title, M + 6, y + 12);
    black();
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    pdf.setFont("helvetica", "normal");
    y += 23;
  };

  /* ── Plain heading (matches on-screen h3.pd-section-title — bold ink text,
     no boxed bar), used for "Permit Types" which isn't a bordered card header. ── */
  const sectionTitlePlain = (title) => {
    y += 18;
    pageBreak(24);
    pdf.setFontSize(13); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
    pdf.text(title, M, y);
    black(); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    y += 18;
  };

  /* ── Full-width bordered card with a title bar + wrapped paragraph body,
     used for the Job Description card. ── */
  const textCard = (title, text) => {
    const pad = 10, headerH = 22;
    const bodyLines = pdf.splitTextToSize(str(text || "-"), PW - M * 2 - pad * 2);
    const cardH = headerH + pad * 1.6 + bodyLines.length * 13;
    pageBreak(cardH + 14);
    pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
    pdf.roundedRect(M, y, PW - M * 2, cardH, 4, 4);
    pdf.setFillColor(...PD_SURFACE_2);
    pdf.rect(M + 0.5, y + 0.5, PW - M * 2 - 1, headerH - 0.5, "F");
    pdf.setDrawColor(...PD_LINE); pdf.line(M, y + headerH, PW - M, y + headerH);
    pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_MUTED);
    pdf.text(title.toUpperCase(), M + pad, y + 15);
    let ty = y + headerH + pad + 6;
    pdf.setFontSize(10); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INK);
    bodyLines.forEach((l) => { pdf.text(l, M + pad, ty); ty += 13; });
    black();
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    y += cardH + 14;
  };

  /* ── Write a wrapped line ── */
  const wl = (text, x, maxW) => {
    const lines = pdf.splitTextToSize(str(text), maxW || PW - M * 2);
    lines.forEach((l) => { pageBreak(LH + 2); pdf.text(l, x, y); y += LH + 2; });
  };

  /* ── Two-column key: value row ── */
  const row = (lKey, lVal, rKey, rVal, opts = {}) => {
    pdf.setFontSize(10);
    const lText = lKey ? `${lKey}: ${str(lVal)}` : "";
    const rText = rKey ? `${rKey}: ${str(rVal)}` : "";
    const lLines = pdf.splitTextToSize(lText, CW);
    const rLines = pdf.splitTextToSize(rText, CW);
    const mx = Math.max(lLines.length, rLines.length, 1);
    for (let i = 0; i < mx; i++) {
      pageBreak();
      black();
      if (i < lLines.length) {
        if (opts.lColor && i === 0 && lKey) {
          const lbl = `${lKey}: `; pdf.text(lbl, M, y);
          pdf.setTextColor(...opts.lColor); pdf.setFont("helvetica", "bold");
          pdf.text(str(lVal), M + pdf.getTextWidth(lbl), y);
          pdf.setFont("helvetica", "normal"); black();
        } else pdf.text(lLines[i], M, y);
      }
      const RX = M + CW + GAP;
      if (i < rLines.length) {
        if (opts.rColor && i === 0 && rKey) {
          const lbl = `${rKey}: `; pdf.text(lbl, RX, y);
          pdf.setTextColor(...opts.rColor); pdf.setFont("helvetica", "bold");
          pdf.text(str(rVal), RX + pdf.getTextWidth(lbl), y);
          pdf.setFont("helvetica", "normal"); black();
        } else pdf.text(rLines[i], RX, y);
      }
      y += LH + 2;
    }
  };

  /* ── Bold label + normal value (full width) ── */
  const kv = (key, val, indent = M) => {
    pageBreak(LH + 2);
    pdf.setFontSize(10); pdf.setFont("helvetica", "bold"); black();
    const lbl = `${key}: `;
    pdf.text(lbl, indent, y);
    const lw = pdf.getTextWidth(lbl);
    pdf.setFont("helvetica", "normal");
    const maxW = PW - indent - lw - M;
    const lines = pdf.splitTextToSize(str(val), maxW > 80 ? maxW : PW - M * 2);
    pdf.text(lines[0], indent + lw, y); y += LH + 2;
    for (let i = 1; i < lines.length; i++) { pageBreak(LH + 2); pdf.text(lines[i], indent + lw, y); y += LH + 2; }
  };

  /* ── Colored inline status value ── */
  const colorVal = (key, val, color, indent = M) => {
    pageBreak(LH + 2);
    pdf.setFontSize(10); black();
    const lbl = `${key}: `;
    pdf.text(lbl, indent, y);
    pdf.setTextColor(...color); pdf.setFont("helvetica", "bold");
    pdf.text(str(val), indent + pdf.getTextWidth(lbl), y);
    pdf.setFont("helvetica", "normal"); black();
    y += LH + 2;
  };

  /* ── Divider line ── */
  const divider = (indent = M) => {
    pageBreak(8);
    pdf.setDrawColor(210, 210, 210);
    pdf.setLineWidth(0.3);
    pdf.line(indent, y, PW - M, y);
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.2);
    y += 8;
  };

  /* ── Risk assessment: bordered 2-column "STEP n" cards ── */
  const riskCardGrid = (items) => {
    const cols = 2, gap = 10, pad = 8;
    const boxW = (PW - M * 2 - gap * (cols - 1)) / cols;
    const textW = boxW - pad * 2;
    for (let i = 0; i < items.length; i += cols) {
      const rowItems = items.slice(i, i + cols);
      const measured = rowItems.map((it, ci) => {
        const stepLabel = `STEP ${i + ci + 1}`;
        const taskLines = pdf.splitTextToSize(str(it.taskDescription || "-"), textW);
        const hazardName = it.hazardName || (it.hazard && (it.hazard.name || it.hazard.hazardName)) || "-";
        const hazardLines = pdf.splitTextToSize(hazardName, textW - pdf.getTextWidth("Hazard: "));
        const controls = [...(it.controlNames || []), ...(it.otherControlText ? [it.otherControlText] : [])];
        const controlLines = controls.length > 0
          ? controls.flatMap((c) => pdf.splitTextToSize(`•  ${c}`, textW - 4))
          : ["—"];
        const respName = it.responsiblePersonName || "-";
        const respLines = pdf.splitTextToSize(respName, textW - pdf.getTextWidth("Responsible: "));
        const h = pad + 12 + (taskLines.length * 12) + 6 + (hazardLines.length * 11) + 8 + 11
          + (controlLines.length * 11) + 10 + (respLines.length * 11) + pad;
        return { stepLabel, taskLines, hazardLines, controlLines, respName, respLines, h };
      });
      const rowH = Math.max(...measured.map((m) => m.h), 60);
      pageBreak(rowH + 8);
      measured.forEach((m, ci) => {
        const bx = M + ci * (boxW + gap);
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        pdf.roundedRect(bx, y, boxW, rowH, 3, 3);
        pdf.setFillColor(...PD_ACCENT);
        pdf.rect(bx, y + 1, 2.5, rowH - 2, "F");
        let ty = y + pad + 8;
        pdf.setFontSize(8); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_ACCENT);
        pdf.text(m.stepLabel, bx + pad, ty); ty += 12;
        pdf.setFontSize(9.5); pdf.setTextColor(...PD_INK); pdf.setFont("helvetica", "bold");
        m.taskLines.forEach((l) => { pdf.text(l, bx + pad, ty); ty += 12; }); ty += 6;
        pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        const hazardLbl = "Hazard: ";
        pdf.text(hazardLbl, bx + pad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        m.hazardLines.forEach((l, li) => { pdf.text(l, bx + pad + (li === 0 ? pdf.getTextWidth(hazardLbl) : 0), ty); ty += 11; });
        ty += 8;
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(8); pdf.setTextColor(...PD_MUTED);
        pdf.text("CONTROL MEASURES", bx + pad, ty); ty += 11;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...PD_INK);
        m.controlLines.forEach((l) => { pdf.text(l, bx + pad + 2, ty); ty += 11; }); ty += 4;
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
        pdf.line(bx + pad, ty, bx + boxW - pad, ty);
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
        ty += 10;
        pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        const respLbl = "Responsible: ";
        pdf.text(respLbl, bx + pad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        m.respLines.forEach((l, li) => { pdf.text(l, bx + pad + (li === 0 ? pdf.getTextWidth(respLbl) : 0), ty); ty += 11; });
      });
      black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
      y += rowH + 10;
    }
  };

  /* ── Clearance departments: bordered 3-column cards ── */
  const clearanceCardGrid = (depts) => {
    const cols = 3, gap = 10, pad = 8;
    const boxW = (PW - M * 2 - gap * (cols - 1)) / cols;
    const textW = boxW - pad * 2;
    for (let i = 0; i < depts.length; i += cols) {
      const rowItems = depts.slice(i, i + cols);
      const measured = rowItems.map((d) => {
        const remarkLines = pdf.splitTextToSize(`Remark: ${d.remark || "-"}`, textW);
        const byLine = d.approvedByName ? `By: ${d.approvedByName}` : null;
        const h = pad + 14 + (byLine ? 11 : 0) + (remarkLines.length * 11) + pad;
        return { d, byLine, remarkLines, h };
      });
      const rowH = Math.max(...measured.map((m) => m.h), 50);
      pageBreak(rowH + 8);
      measured.forEach((m, ci) => {
        const bx = M + ci * (boxW + gap);
        const statusLabel = m.d.approved == null ? "Pending" : (m.d.approved ? "Approved" : "Rejected");
        const statusColorRgb = m.d.approved == null ? PD_WARN : (m.d.approved ? PD_OK : PD_CRIT);
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        pdf.roundedRect(bx, y, boxW, rowH, 3, 3);
        pdf.setFillColor(...statusColorRgb);
        pdf.rect(bx, y + 1, 2.5, rowH - 2, "F");
        let ty = y + pad + 9;
        pdf.setFontSize(9.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        const deptName = pdf.splitTextToSize(m.d.departmentName || "-", textW * 0.55)[0];
        pdf.text(deptName, bx + pad, ty);
        pill(statusLabel, statusColorRgb, bx + boxW - pad - (pdf.getTextWidth(statusLabel) + 20), ty, { fontSize: 7.5 });
        ty += 13;
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(...PD_MUTED);
        if (m.byLine) { pdf.text(m.byLine, bx + pad, ty); ty += 11; }
        m.remarkLines.forEach((l) => { pdf.text(l, bx + pad, ty); ty += 11; });
      });
      black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
      y += rowH + 10;
    }
  };

  /* ── Generic "connected" section: a single continuous bordered card holding
     a boxed title bar (like Job Description) and, below it, an N-per-row grid
     of bordered mini-cards. Used for Shift-wise Revalidations, Isolation/LOTO,
     Gas Tests, Tool Box Talks and Shift Handovers so each reads as one card
     instead of a header floating above separately bordered table rows.
     `measuredItems` must each carry a numeric `h` (content height) computed
     with the SAME offsets `drawItem` uses, so the two passes can't drift.
     `drawItem(bx, topY, colW, item)` draws one mini-card's content (the
     bordered box + optional left accent strip are drawn by this helper). */
  const connectedCardSection = (title, cols, measuredItems, drawItem) => {
    const outerPad = 10, headerH = 22, gap = 10;
    const colW = (PW - M * 2 - outerPad * 2 - gap * (cols - 1)) / cols;
    const rows = [];
    for (let i = 0; i < measuredItems.length; i += cols) rows.push(measuredItems.slice(i, i + cols));

    y += SECTION_MB;
    pageBreak(headerH + Math.max(...rows[0].map((r) => r.h)) + outerPad * 2 + 10);
    const cardStartY = y;
    const cardStartPage = pageNum;
    pdf.setFillColor(...PD_SURFACE_2); pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
    pdf.rect(M, y, PW - M * 2, headerH, "F");
    pdf.line(M, y + headerH, PW - M, y + headerH);
    pdf.setTextColor(...PD_MUTED); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.text(title.toUpperCase(), M + 9, y + 15);
    black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
    y += headerH + outerPad;

    rows.forEach((row) => {
      const rowH = Math.max(...row.map((r) => r.h));
      pageBreak(rowH + gap);
      row.forEach((item, ci) => {
        const bx = M + outerPad + ci * (colW + gap);
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        pdf.roundedRect(bx, y, colW, rowH, 3, 3);
        if (item.accentColor) {
          pdf.setFillColor(...item.accentColor);
          pdf.rect(bx, y + 1, 2.5, rowH - 2, "F");
        }
        drawItem(bx, y, colW, item);
      });
      y += rowH + gap;
    });
    black(); pdf.setFontSize(10); pdf.setFont("helvetica", "normal");

    /* Connected outer border, drawn in per-page pieces if the grid spans a
       page break (same technique as the permit type / meeting log cards). */
    const cardEndY = y - gap + outerPad, cardEndPage = pageNum;
    const boxX = M, boxW = PW - M * 2;
    if (cardEndPage === cardStartPage) {
      pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
      pdf.roundedRect(boxX, cardStartY, boxW, cardEndY - cardStartY, 4, 4);
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    } else {
      const topY = 56, bottomY = PH - 58;
      for (let p = cardStartPage; p <= cardEndPage; p++) {
        pdf.setPage(p);
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        if (p === cardStartPage) {
          pdf.line(boxX, cardStartY, boxX + boxW, cardStartY);
          pdf.line(boxX, cardStartY, boxX, bottomY);
          pdf.line(boxX + boxW, cardStartY, boxX + boxW, bottomY);
        } else if (p === cardEndPage) {
          pdf.line(boxX, topY, boxX, cardEndY);
          pdf.line(boxX + boxW, topY, boxX + boxW, cardEndY);
          pdf.line(boxX, cardEndY, boxX + boxW, cardEndY);
        } else {
          pdf.line(boxX, topY, boxX, bottomY);
          pdf.line(boxX + boxW, topY, boxX + boxW, bottomY);
        }
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      }
      pdf.setPage(cardEndPage);
    }
    y += 14;
  };

  /* ════════════════════════════════════════
     START
  ════════════════════════════════════════ */
  pageHeader();
  topInfoBar();

  /* Gas Test Required warning */
  const gasTestMandatory = (resolvedBlocks || []).some((b) => b.gasTestMandatory);
  if (gasTestMandatory && ["ISSUED", "ACTIVE"].includes(permit?.status)) {
    const sortedForBanner = [...gasTests].sort((a, b) => new Date(b.testedAt || 0) - new Date(a.testedAt || 0));
    const latestGasTest = sortedForBanner[0];
    const now = new Date();
    const hasValidGasTest = gasTests.length > 0 && (!latestGasTest.validUntil || new Date(latestGasTest.validUntil) >= now);
    if (!hasValidGasTest) {
      const msg = gasTests.length === 0
        ? "No gas test has been recorded for this permit."
        : `Gas test expired at ${fmtDt(latestGasTest.validUntil)}.`;
      const action = permit.status === "ISSUED"
        ? "A valid gas test is required before this permit can be activated."
        : "Fresh testing required immediately or suspend work.";
      pageBreak(30);
      pdf.setFillColor(...lighten(PD_CRIT, 0.88));
      pdf.roundedRect(M, y, PW - M * 2, 30, 2, 2, "F");
      pdf.setTextColor(...PD_CRIT); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
      pdf.text("Gas Test Required", M + 8, y + 13);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9);
      pdf.text(`${msg} ${action}`, M + 8, y + 24);
      pdf.setFont("helvetica", "normal"); black(); y += 38;
    }
  }

  /* SIMOPS warning */
  if (simopsConflicts?.length > 0) {
    pdf.setFillColor(...lighten(PD_WARN, 0.88));
    pdf.roundedRect(M, y, PW - M * 2, 22, 2, 2, "F");
    pdf.setTextColor(...PD_WARN); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.text(`SIMOPS WARNING: ${simopsConflicts.length} concurrent permit conflict(s) detected`, M + 8, y + 14);
    pdf.setFont("helvetica", "normal"); black(); y += 30;
  }

  /* Retention notice */
  if (["CLOSED", "CANCELLED"].includes(permit?.status) && permit?.retentionExpiresAt) {
    pdf.setFillColor(...lighten(PD_INFO, 0.88));
    pdf.roundedRect(M, y, PW - M * 2, 22, 2, 2, "F");
    pdf.setTextColor(...PD_INFO); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.text(`Mandatory Retention — cannot be deleted before ${fmtDate(permit.retentionExpiresAt)} (1-year regulatory requirement)`, M + 8, y + 14);
    pdf.setFont("helvetica", "normal"); black(); y += 30;
  }

  /* Confined Space Box-Up Clearance notice */
  const hasCSEType = (resolvedBlocks || []).some((b) => b.permitTypeCode === "CSE");
  if (hasCSEType) {
    if (permit?.boxUpClearedAt) {
      pdf.setFillColor(...lighten(PD_OK, 0.88));
      pdf.roundedRect(M, y, PW - M * 2, 22, 2, 2, "F");
      pdf.setTextColor(...PD_OK); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
      pdf.text(`✓ Confined Space Box-Up Clearance: recorded by ${str(permit.boxUpClearedBy)} at ${fmtDt(permit.boxUpClearedAt)}`, M + 8, y + 14);
      pdf.setFont("helvetica", "normal"); black();
      y += 22;
      if (permit.boxUpRemark) { kv("Remark", permit.boxUpRemark, M + 8); }
      y += 8;
    } else {
      pdf.setFillColor(...lighten(PD_WARN, 0.88));
      pdf.roundedRect(M, y, PW - M * 2, 30, 2, 2, "F");
      pdf.setTextColor(...PD_WARN); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
      pdf.text("Confined Space — Box-Up Clearance Required", M + 8, y + 13);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(9);
      pdf.text("Before closing this Confined Space permit, a responsible person must certify that all workers", M + 8, y + 24);
      pdf.setFont("helvetica", "normal"); black(); y += 38;
    }
  }

  /* ════════════════ 2. LOCATION / STATUS / TIMELINE (combined summary row) ════════════════ */
  const secondRowCells = [
    ["LOCATION", str(permit?.locationName)],
    ["STATUS", str(permit?.status).replace(/_/g, " "), statusColor(permit?.status)],
    ["AUTO REJECT ON SCHEDULE TIME", permit?.permitAutoRejectOnScheduleTime ? "Yes" : "No"],
    ["REQUIRES PERIODIC REVALIDATION", permit?.revalidationRequired ? `Yes - every ${permit?.revalidationIntervalHours || 8}h` : "No"],
    ["SUSPEND UNTIL", permit?.suspendUntil ? fmtDt(permit.suspendUntil) : "N/A"],
    ["EXTENDED UNTIL", permit?.extendUntil ? fmtDt(permit.extendUntil) : "N/A"],
    ["CREATED BY / UPDATED BY", `${str(permit?.createdBy)} -> ${str(permit?.updatedBy)}`],
    ["DATE CREATED / UPDATED", `${fmtDt(permit?.createdDate)} -> ${fmtDt(permit?.updatedAt)}`],
  ];
  if (permit?.suspendedAt) secondRowCells.push(["SUSPENDED AT", fmtDt(permit.suspendedAt)]);
  if (permit?.suspendedBy) secondRowCells.push(["SUSPENDED BY", str(permit.suspendedBy)]);
  if (permit?.resumedAt) secondRowCells.push(["RESUMED AT", fmtDt(permit.resumedAt)]);
  if (permit?.resumedBy) secondRowCells.push(["RESUMED BY", str(permit.resumedBy)]);
  gridInfoBar(secondRowCells, 8);

  if (permit.resumptionRemark) kv("Resumption Remark", permit.resumptionRemark);

  textCard("Job Description", permit?.jobDescription);

  if (permit?.comments?.length > 0) {
    y += 6;
    pdf.setFont("helvetica", "bold"); wl("Return Comments:", M); pdf.setFont("helvetica", "normal");
    permit.comments.forEach((c, i) => wl(`${i + 1}. [${fmtDt(c.timestamp)}] ${c.by || ""}: ${c.text || c}`, M + 10));
  }

  /* ════════════════ 3. PERMIT TYPES & CHECKLISTS ════════════════ */
  if (resolvedBlocks.length > 0) {
    sectionTitlePlain("Permit Types");
    resolvedBlocks.forEach((block) => {
      /* Reserve enough room for the card header + field row + the start of the
         checklist before beginning a new card — if that doesn't fit in what's
         left on the page, push the whole card to the next page instead of
         starting it and immediately splitting a few lines in. */
      pageBreak(140);
      y += 14;
      pageBreak(20);
      const cardStartY = y - 6;
      const cardStartPage = pageNum;
      const typeColorHex = block.permitTypeColor || "#6b7280";
      const tc = [
        parseInt(typeColorHex.slice(1, 3), 16) || 107,
        parseInt(typeColorHex.slice(3, 5), 16) || 114,
        parseInt(typeColorHex.slice(5, 7), 16) || 128,
      ];
      const pad = 10;
      const pw = pill((block.permitTypeName || "-").toUpperCase(), tc, M + pad, y + 10, { fontSize: 9.5 });
      pdf.setFontSize(9); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      const rrLbl = "Residual Risk: ";
      pdf.text(rrLbl, M + pad + pw + 10, y + 10);
      pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(str(block.residualRiskLevel || "-"), M + pad + pw + 10 + pdf.getTextWidth(rrLbl), y + 10);
      black(); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
      y += 18;
      pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
      pdf.line(M, y, PW - M, y);
      y += 20;

      /* 3-column field row: Contractor / Equipment ID / Work Order ID */
      const fieldColW = (PW - M * 2 - pad * 2) / 3;
      pageBreak(28);
      [
        ["CONTRACTOR", str(block.contractorName || "N/A")],
        ["EQUIPMENT ID", str(block.equipmentId || "N/A")],
        ["WORK ORDER ID", str(block.externalWorkOrderId || "N/A")],
      ].forEach(([label, val], i) => {
        const cx = M + pad + i * fieldColW;
        pdf.setFontSize(7.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_MUTED);
        pdf.text(label, cx, y);
        pdf.setFontSize(9.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INK);
        pdf.text(val, cx, y + 12);
      });
      black(); pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
      y += 28;

      if (block.checklistItems?.length > 0) {
        const items = block.checklistItems.filter((item) => item.mandatory);
        const chipH = 16, chipPad = 7, chipGap = 6;
        const tagText = "REQUIRED";
        pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold");
        const tagW = pdf.getTextWidth(tagText) + 18;
        const availW = PW - M - pad - M - tagW - 8;

        /* Build each field's chip label into a {label, val} pair, then lay
           the chips out into wrapped rows against the available width — done
           once up front so the exact same row layout can both be measured
           (to reserve space) and drawn (no drift between the two passes). */
        const buildChips = (item) => {
          const fv = item.fieldsValues || {};
          return (item.fields || [])
            .map((f) => {
              let val = fv[f.fieldName];
              if (val === undefined || val === null || val === "") return null;
              if (val === true || val === "true") val = "Yes";
              else if (val === false || val === "false") val = "No";
              return { label: f.label || f.fieldName, val: str(val) };
            })
            .filter(Boolean);
        };
        const layoutChips = (chips) => {
          pdf.setFontSize(8.5);
          const rows = [[]];
          let cx = M + pad;
          chips.forEach((chip) => {
            pdf.setFont("helvetica", "normal");
            const labelW = pdf.getTextWidth(`${chip.label}:  `);
            pdf.setFont("helvetica", "bold");
            const valW = pdf.getTextWidth(chip.val);
            const cw = labelW + valW + chipPad * 2;
            if (cx + cw > PW - M) { rows.push([]); cx = M + pad; }
            rows[rows.length - 1].push({ ...chip, labelW, cw });
            cx += cw + chipGap;
          });
          return rows;
        };
        const measureItem = (item) => {
          pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
          const lines = pdf.splitTextToSize(str(item.text), availW);
          const chips = buildChips(item);
          const rows = chips.length > 0 ? layoutChips(chips) : [];
          const h = lines.length * LH + (rows.length > 0 ? 4 + rows.length * (chipH + 5) + 2 : 0) + 10;
          return { lines, rows, h };
        };

        pageBreak(16 + measureItem(items[0]).h);
        pdf.setFontSize(7.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_MUTED);
        pdf.text("CHECKLIST", M + pad, y);
        black(); pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
        y += 16;

        items.forEach((item, idx) => {
          const { lines, rows, h } = measureItem(item);
          pageBreak(h);
          pdf.setFontSize(10); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INK);
          lines.forEach((line, li) => {
            pdf.text(line, M + pad, y);
            if (li === 0) pill(tagText, PD_WARN, PW - M - pad - tagW, y + 3);
            y += LH;
          });

          if (rows.length > 0) {
            y += 4;
            rows.forEach((row) => {
              let cx = M + pad;
              row.forEach(({ label, val, labelW, cw }) => {
                pdf.setDrawColor(...PD_LINE); pdf.setFillColor(...PD_SURFACE_2);
                pdf.roundedRect(cx, y - chipH + 4, cw, chipH, 3, 3, "FD");
                pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); pdf.setTextColor(...PD_MUTED);
                pdf.text(`${label}:  `, cx + chipPad, y);
                pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
                pdf.text(val, cx + chipPad + labelW, y);
                cx += cw + chipGap;
              });
              y += chipH + 5;
            });
            black(); pdf.setDrawColor(0, 0, 0);
            pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
            y -= 3;
          }
          y += 10;
          if (idx < items.length - 1) {
            pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
            pdf.line(M + pad, y, PW - M - pad, y);
            pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
            y += 10;
          }
        });
      }

      if (block.permitTypeName?.toLowerCase().includes("confined space")) {
        y += 6;
        const cpAccLabel = block.competentPersonApproved ? "Approved" : "Pending";
        const cpText = `Competent Person name: ${str(block.competentPersonName || "N/A")}  •  Acceptance: ${cpAccLabel}`;
        const cpLines = pdf.splitTextToSize(cpText, PW - M * 2 - pad * 2 - 16);
        const cpBoxH = 18 + cpLines.length * 13 + 8;
        pageBreak(cpBoxH + 8);
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        pdf.roundedRect(M + pad, y, PW - M * 2 - pad * 2, cpBoxH, 3, 3);
        pdf.setFontSize(9.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text("Competent Person", M + pad + 8, y + 16);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(9); pdf.setTextColor(...PD_MUTED);
        let cpTy = y + 30;
        cpLines.forEach((l) => { pdf.text(l, M + pad + 8, cpTy); cpTy += 13; });
        black(); pdf.setFontSize(10);
        y += cpBoxH + 6;
      }

      /* Reviewer Remark / Acceptance: boxed side-by-side fields, matching the
         on-screen bordered inputs (not inline "Label: value" text). "By:" (the
         approver name) sits INSIDE the Acceptance box, under the pill. */
      y += 6;
      const boxGap = 16;
      const boxColW = (PW - M * 2 - pad * 2 - boxGap) / 2;
      const boxH = block.approvedByName ? 46 : 32;
      pageBreak(boxH + 20);
      pdf.setFontSize(7.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_MUTED);
      pdf.text("REVIEWER REMARK", M + pad, y);
      pdf.text("ACCEPTANCE", M + pad + boxColW + boxGap, y);
      y += 8;
      pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
      pdf.roundedRect(M + pad, y, boxColW, boxH, 3, 3);
      pdf.setFontSize(9.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INK);
      const remarkLines = pdf.splitTextToSize(str(block.reviewerRemark || "-"), boxColW - 14);
      pdf.text(remarkLines[0] || "-", M + pad + 8, y + 19);
      pdf.roundedRect(M + pad + boxColW + boxGap, y, boxColW, boxH, 3, 3);
      const accLabel = block.approved == null ? "Pending" : (block.approved ? "Approved" : "Rejected");
      const accColor = block.approved == null ? PD_MUTED : (block.approved ? PD_OK : PD_CRIT);
      pill(accLabel, accColor, M + pad + boxColW + boxGap + 8, y + 20);
      if (block.approvedByName) {
        pdf.setFontSize(8.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        pdf.text(`By: ${block.approvedByName}`, M + pad + boxColW + boxGap + 8, y + 34);
      }
      black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
      y += boxH + 12;

      /* Card border: single rounded rect if the block fit on one page; if it
         spanned a page break, draw the border in per-page pieces (top page
         open at the bottom, middle pages just side rules, last page open at
         the top) by switching the active page with setPage() and drawing on
         each in turn, then restoring the current page before continuing. */
      const cardEndY = y, cardEndPage = pageNum;
      const boxX = M, boxW = PW - M * 2;
      if (cardEndPage === cardStartPage) {
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        pdf.roundedRect(boxX, cardStartY - 6, boxW, cardEndY - cardStartY + 6, 4, 4);
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      } else {
        const topY = 56, bottomY = PH - 58;
        for (let p = cardStartPage; p <= cardEndPage; p++) {
          pdf.setPage(p);
          pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
          if (p === cardStartPage) {
            pdf.line(boxX, cardStartY - 6, boxX + boxW, cardStartY - 6);
            pdf.line(boxX, cardStartY - 6, boxX, bottomY);
            pdf.line(boxX + boxW, cardStartY - 6, boxX + boxW, bottomY);
          } else if (p === cardEndPage) {
            pdf.line(boxX, topY, boxX, cardEndY + 6);
            pdf.line(boxX + boxW, topY, boxX + boxW, cardEndY + 6);
            pdf.line(boxX, cardEndY + 6, boxX + boxW, cardEndY + 6);
          } else {
            pdf.line(boxX, topY, boxX, bottomY);
            pdf.line(boxX + boxW, topY, boxX + boxW, bottomY);
          }
          pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
        }
        pdf.setPage(cardEndPage);
      }
      y += 16;
    });
  }

  /* ════════════════ 4. RISK ASSESSMENT ════════════════ */
  const overallRisk = resolvedRA?.overallRisk;
  if (resolvedRiskItems.length > 0) {
    y += 18;
    pageBreak(24);
    pdf.setFontSize(13); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
    const titleText = "Risk Assessment & Job Safety Analysis";
    pdf.text(titleText, M, y);
    if (overallRisk) {
      const tx = M + pdf.getTextWidth(titleText) + 10;
      pdf.setFontSize(9.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      pdf.text(`Likelihood: ${overallRisk.likelihood}, Severity: ${overallRisk.severity} (${overallRisk.riskLevel})`, tx, y);
    }
    black(); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
    y += 20;
    riskCardGrid(resolvedRiskItems);
  } else if (overallRisk) {
    /* No risk items — show the highlighted "No risk assessment found" notice
       inside a bordered card with a boxed header (like Job Description),
       instead of a bare heading + a floating unbordered banner. */
    const pad = 10, headerH = 22, noticeH = 26;
    const cardH = headerH + pad + noticeH + pad;
    pageBreak(cardH + 14);
    pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
    pdf.roundedRect(M, y, PW - M * 2, cardH, 4, 4);
    pdf.setFillColor(...PD_SURFACE_2);
    pdf.rect(M + 0.5, y + 0.5, PW - M * 2 - 1, headerH - 0.5, "F");
    pdf.line(M, y + headerH, PW - M, y + headerH);
    pdf.setTextColor(...PD_MUTED); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.text("RISK ASSESSMENT", M + pad, y + 15);
    pdf.setFontSize(9); pdf.setFont("helvetica", "normal");
    const riskStatsText = `Likelihood: ${overallRisk.likelihood}, Severity: ${overallRisk.severity} (${overallRisk.riskLevel})`;
    pdf.text(riskStatsText, PW - M - pad - pdf.getTextWidth(riskStatsText), y + 15);
    black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    pdf.setFontSize(10); pdf.setFont("helvetica", "normal");

    const noticeY = y + headerH + pad;
    pdf.setFillColor(...lighten(PD_INFO, 0.9));
    pdf.roundedRect(M + pad, noticeY, PW - M * 2 - pad * 2, noticeH, 4, 4, "F");
    pdf.setDrawColor(...PD_INFO); pdf.setLineWidth(1);
    pdf.triangle(M + pad + 15, noticeY + 9, M + pad + 9, noticeY + 20, M + pad + 21, noticeY + 20, "D");
    pdf.setFontSize(7.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INFO);
    pdf.text("!", M + pad + 14, noticeY + 18);
    pdf.setFontSize(10); pdf.setTextColor(...PD_INK);
    pdf.text("No risk assessment found", M + pad + 30, noticeY + 17);
    black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2); pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);

    y += cardH + 14;
  }

  /* ════════════════ 5. ISSUER DEPT HOD APPROVAL ════════════════ */
  if (permit?.hodApproved !== null && permit?.hodApproved !== undefined) {
    const pad = 10, headerH = 22, bodyH = 40;
    const cardH = headerH + bodyH;
    pageBreak(cardH + 14);
    pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
    pdf.roundedRect(M, y, PW - M * 2, cardH, 4, 4);
    pdf.setFillColor(...PD_SURFACE_2);
    pdf.rect(M + 0.5, y + 0.5, PW - M * 2 - 1, headerH - 0.5, "F");
    pdf.setDrawColor(...PD_LINE); pdf.line(M, y + headerH, PW - M, y + headerH);
    pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_MUTED);
    pdf.text("ISSUER DEPT HOD APPROVAL", M + pad, y + 15);

    const cy = y + headerH + 25;
    const hodLabel = permit.hodApproved ? "Approved" : "Rejected";
    const hodColor = permit.hodApproved ? PD_OK : PD_CRIT;
    const pw = pill(hodLabel, hodColor, M + pad, cy);
    let hx = M + pad + pw + 10;
    pdf.setFontSize(9.5);
    if (permit.hodApprovedBy) {
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      pdf.text("by ", hx, cy); hx += pdf.getTextWidth("by ");
      pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(permit.hodApprovedBy, hx, cy); hx += pdf.getTextWidth(permit.hodApprovedBy) + 8;
    }
    if (permit.hodApprovalRemark) {
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      pdf.text("·  ", hx, cy); hx += pdf.getTextWidth("·  ");
      pdf.text(`Remark: ${permit.hodApprovalRemark}`, hx, cy);
    }
    black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
    y += cardH + 14;
  }

  /* ════════════════ 6. CLEARANCE DEPARTMENTS ════════════════ */
  if (permit?.clearanceDepartments?.length > 0) {
    section("Clearance Departments");
    const grouped = {};
    permit.clearanceDepartments.forEach((c) => {
      if (!grouped[c.cycleNo]) grouped[c.cycleNo] = [];
      grouped[c.cycleNo].push(c);
    });
    Object.keys(grouped).forEach((cycle) => {
      const cd = grouped[cycle];
      const allApproved = cd.every((c) => c.approved === true);
      const anyPending = cd.some((c) => c.approved == null);
      const cycleStatusText = allApproved ? "All approved" : (anyPending ? "Pending" : "Has rejections");
      subHead(`${cd[0]?.approvalType || "INITIAL"} — Cycle ${cycle}    (${cycleStatusText})`);
      clearanceCardGrid(cd);
    });
  }

  /* ════════════════ 7. SHIFT-WISE REVALIDATIONS ════════════════ */
  if (revalidations.length > 0) {
    const cols = 3, mPad = 8, lineH = 11;
    const colW = (PW - M * 2 - 20 - 10 * (cols - 1)) / cols;
    const innerW = colW - mPad * 2;
    const measured = revalidations.map((rev) => {
        pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold");
        const issuerLbl = "Issuer Signed: ", issuerLblW = pdf.getTextWidth(issuerLbl);
        const validLbl = "Valid Until: ", validLblW = pdf.getTextWidth(validLbl);
        const receiverLbl = "Receiver Signed: ";
        const issuerLines = pdf.splitTextToSize(rev.issuerSignedByName || "-", innerW - issuerLblW);
        const validLines = pdf.splitTextToSize(fmtDt(rev.validUntil), innerW - validLblW);
        const remarkLines = rev.issuerRemark ? pdf.splitTextToSize(rev.issuerRemark, innerW) : [];
        const receiverPending = !rev.receiverSignedAt;
        const receiverText = receiverPending ? "Pending" : (rev.receiverSignedByName || "-");
        const receiverColor = receiverPending ? PD_WARN : PD_OK;
        const accentColor = receiverPending ? PD_WARN : PD_OK;
        /* Mirrors the exact draw-sequence increments below: top inset, title
           row, Valid Until block + gap, divider gap, Issuer Signed block
           (+ signed-at timestamp + italic remark if present) + gap, divider
           gap, then the Receiver Signed line + bottom pad. */
        const h = (mPad + 8) + 15
          + (validLines.length * lineH + 6) + 14
          + (issuerLines.length * lineH
            + (rev.issuerSignedAt ? 10 : 0)
            + (remarkLines.length > 0 ? remarkLines.length * lineH + 2 : 0) + 6) + 14
          + mPad;
        return { rev, issuerLbl, issuerLblW, validLbl, validLblW, receiverLbl, issuerLines, validLines, remarkLines, receiverPending, receiverText, receiverColor, h, accentColor };
      });
      connectedCardSection("Shift-wise Revalidations", cols, measured, (bx, topY, cw, item) => {
        const { rev, issuerLbl, issuerLblW, validLbl, validLblW, receiverLbl, issuerLines, validLines, remarkLines, receiverText, receiverColor } = item;
        let ty = topY + mPad + 8;
        pdf.setFontSize(10); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(`Shift ${rev.shiftNo ?? "-"} — ${rev.shiftLabel || "-"}`, bx + mPad, ty);
        pdf.setFontSize(8); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        const dtText = rev.revalidationDate || "-";
        pdf.text(dtText, bx + cw - mPad - pdf.getTextWidth(dtText), ty);
        ty += 15;

        pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(validLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        validLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? validLblW : 0), ty); ty += lineH; });
        ty += 6;

        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
        pdf.line(bx + mPad, ty, bx + cw - mPad, ty);
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
        ty += 14;

        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(issuerLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        issuerLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? issuerLblW : 0), ty); ty += lineH; });
        if (rev.issuerSignedAt) {
          pdf.setFontSize(7.5); pdf.setTextColor(...PD_MUTED);
          pdf.text(fmtDt(rev.issuerSignedAt), bx + mPad, ty); ty += 10;
          pdf.setFontSize(8.5);
        }
        if (remarkLines.length > 0) {
          pdf.setFont("helvetica", "italic"); pdf.setTextColor(...PD_MUTED);
          remarkLines.forEach((l) => { pdf.text(l, bx + mPad, ty); ty += lineH; });
          pdf.setFont("helvetica", "normal");
          ty += 2;
        }
        ty += 6;

        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
        pdf.line(bx + mPad, ty, bx + cw - mPad, ty);
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
        ty += 14;

        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(receiverLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...receiverColor);
        pdf.text(receiverText, bx + mPad + pdf.getTextWidth(receiverLbl), ty);

        black();
      });
  }

  /* ════════════════ 9. ISOLATION / LOTO RECORDS ════════════════ */
  if (isolationRecords.length > 0) {
      const cols = 3, mPad = 8, lineH = 11;
      const isoStatusColor = (s) => s === "ISOLATED" ? PD_CRIT : (s === "REINSTATED" ? PD_OK : PD_WARN);
      const measured = isolationRecords.map((rec) => {
        const colW = (PW - M * 2 - 20 - 10 * (cols - 1)) / cols;
        const innerW = colW - mPad * 2;
        pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold");
        const isolatedByLbl = "Isolated By: ", isolatedByLblW = pdf.getTextWidth(isolatedByLbl);
        const methodText = rec.isolationMethod || rec.method || "-";
        const methodW = pdf.getTextWidth(methodText.toUpperCase()) + 14;
        const lockTagText = `Lock/Tag #: ${rec.lockTagNumber || "-"}`;
        const isolatedByLines = pdf.splitTextToSize(rec.isolatedByName || "-", innerW - isolatedByLblW);
        const reinstated = rec.status === "REINSTATED";
        const reinstateLines = reinstated
          ? pdf.splitTextToSize(`${rec.reinstatementByName ? `by ${rec.reinstatementByName}` : ""}${rec.reinstatementRemark ? `  ${rec.reinstatementRemark}` : ""}`, innerW)
          : [];
        const h = (mPad + 8) + 15
          + 20
          + isolatedByLines.length * lineH + 10
          + (reinstated ? reinstateLines.length * lineH + 2 : 0)
          + mPad;
        return { rec, isolatedByLbl, isolatedByLblW, methodText, methodW, lockTagText, isolatedByLines, reinstated, reinstateLines, h, accentColor: isoStatusColor(rec.status) };
      });
      connectedCardSection("Isolation / LOTO Records", cols, measured, (bx, topY, cw, item) => {
        const { rec, isolatedByLbl, isolatedByLblW, methodText, methodW, lockTagText, isolatedByLines, reinstateLines } = item;
        let ty = topY + mPad + 8;
        pdf.setFontSize(10); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        const pointLines = pdf.splitTextToSize(str(rec.isolationPoint || "-"), cw - mPad * 2 - 70);
        pdf.text(pointLines[0] || "-", bx + mPad, ty);
        const statusColorRgb = rec.status === "ISOLATED" ? PD_CRIT : (rec.status === "REINSTATED" ? PD_OK : PD_WARN);
        pill(str(rec.status || "-"), statusColorRgb, bx + cw - mPad - (pdf.getTextWidth(str(rec.status || "-")) + 20), ty, { fontSize: 7.5 });
        ty += 20;

        pdf.setFillColor(...lighten(PD_WARN, 0.85));
        pdf.roundedRect(bx + mPad, ty - 9, methodW, 14, 3, 3, "F");
        pdf.setFontSize(7.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_WARN);
        pdf.text(methodText.toUpperCase(), bx + mPad + 6, ty);
        pdf.setFontSize(8.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        pdf.text(lockTagText, bx + mPad + methodW + 8, ty);
        ty += 10;

        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
        pdf.line(bx + mPad, ty, bx + cw - mPad, ty);
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
        ty += 14;

        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(isolatedByLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        isolatedByLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? isolatedByLblW : 0), ty); ty += lineH; });
        pdf.setFontSize(7.5); pdf.text(fmtDt(rec.isolatedAt), bx + mPad, ty); ty += 10;
        pdf.setFontSize(8.5);

        if (item.reinstated) {
          pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
          reinstateLines.forEach((l) => { pdf.text(l, bx + mPad, ty); ty += lineH; });
        }
        black();
      });
  }

  /* ════════════════ BLANKET PERMIT ════════════════ */
  if (permit?.permitScope === "BLANKET") {
    section("Blanket Permit");
    y += 4; pdf.setFont("helvetica", "bold"); wl("Daily Check-Ins:", M + 8); pdf.setFont("helvetica", "normal");
    if (!blanketCheckins || blanketCheckins.length === 0) {
      pdf.setTextColor(...PD_MUTED); wl("No check-ins recorded yet.", M + 16); black();
    } else {
      blanketCheckins.forEach((ci, i) =>
        wl(`${i + 1}. [${fmtDt(ci.checkinDate || ci.createdAt)}]  By: ${ci.checkedInByName || "-"}  —  ${ci.remark || "-"}`, M + 16));
    }
  }

  /* ════════════════ PERMIT HIERARCHY (Master / Child) ════════════════ */
  if (permit?.parentPermitId) {
    section("Permit Hierarchy");
    kv("Part of Master Permit", `#${permit.parentPermitId}${permit.parentPermitSerialNumber ? ` (${permit.parentPermitSerialNumber})` : ""}`);
  } else if (permit?.childPermits && permit.childPermits.length > 0) {
    const cols = 3, mPad = 8, lineH = 11;
    const measured = permit.childPermits.map((c) => {
      const label = `#${c.id}${c.serialNumber ? ` — ${c.serialNumber}` : ""}`;
      const h = (mPad + 8) + 15 + lineH + mPad;
      return { c, label, h };
    });
    connectedCardSection("Permit Hierarchy", cols, measured, (bx, topY, cw, item) => {
      const { c, label } = item;
      let ty = topY + mPad + 8;
      pdf.setFontSize(9.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      const labelLines = pdf.splitTextToSize(label, cw - mPad * 2 - 60);
      pdf.text(labelLines[0] || label, bx + mPad, ty);
      if (c.status) {
        const statusText = str(c.status).replace(/_/g, " ");
        pill(statusText, statusColor(c.status), bx + cw - mPad - (pdf.getTextWidth(statusText) + 20), ty, { fontSize: 7.5 });
      }
      ty += 15;
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(8.5); pdf.setTextColor(...PD_MUTED);
      pdf.text(str(c.permitTypes || c.jobDescription || "-"), bx + mPad, ty);
      black();
    });
  }

  /* ════════════════ 10. COORDINATION MEETING LOG ════════════════ */
  const sortedMeetings = [...coordinationMeetings].sort((a, b) =>
    new Date(a.meetingDate || 0) - new Date(b.meetingDate || 0));
  if (sortedMeetings.length > 0) {
    const outerPad = 10, headerH = 22;
    const cols = 3, gap = 10, mPad = 8, lineH = 10;
    const colW = (PW - M * 2 - outerPad * 2 - gap * (cols - 1)) / cols;
    const innerW = colW - mPad * 2;
    const gapAfterCondBy = 3, gapAfterAttendees = 4, dividerGap = 10;

    /* Measure every meeting mini-card's height up front (fixed offsets shared
       between measuring and drawing, so they can't drift), then group them
       3-per-row so the whole section — header + every meeting — reads as one
       connected card, like Job Description, instead of a header floating
       above separately bordered rows. */
    const measured = sortedMeetings.map((m) => {
      const attendeeStr = m.attendees && m.attendees.length > 0
        ? m.attendees.map(a =>
            a.employeeName ? a.employeeName : `${a.contractorName}${a.contractorAgency ? ` (${a.contractorAgency})` : ""} [Contractor]`
          ).join(", ")
        : "-";
      pdf.setFontSize(8); pdf.setFont("helvetica", "bold");
      const condByLbl = "Conducted By: ", condByLblW = pdf.getTextWidth(condByLbl);
      const attLbl = "Attendees: ";
      const agendaLbl = "Agenda: ", agendaLblW = pdf.getTextWidth(agendaLbl);
      const minutesLbl = "Minutes: ", minutesLblW = pdf.getTextWidth(minutesLbl);
      const locationLines = pdf.splitTextToSize(str(m.location || "-"), innerW);
      const condByLines = pdf.splitTextToSize(m.conductedByName || "-", innerW - condByLblW);
      const attLines = pdf.splitTextToSize(attendeeStr, innerW);
      const agendaLines = pdf.splitTextToSize(m.agenda || "-", innerW - agendaLblW);
      const minutesLines = pdf.splitTextToSize(m.minutes || "-", innerW - minutesLblW);
      /* Mirrors the exact draw-loop increments below: top inset, location
         lines, the date row, Conducted By (label shares its first value
         line), Attendees (label sits on its OWN line above the wrapped
         value — one extra lineH), the divider gap, then Agenda/Minutes. */
      const topInset = mPad + 8, dateRowH = 12;
      const h = topInset + (locationLines.length * 10) + dateRowH
        + condByLines.length * lineH + gapAfterCondBy
        + lineH + attLines.length * lineH + gapAfterAttendees + dividerGap
        + agendaLines.length * lineH + minutesLines.length * lineH + mPad;
      return { m, locationLines, condByLbl, condByLblW, attLbl, agendaLbl, agendaLblW, minutesLbl, minutesLblW, condByLines, attLines, agendaLines, minutesLines, h };
    });
    const rows = [];
    for (let i = 0; i < measured.length; i += cols) rows.push(measured.slice(i, i + cols));

    y += SECTION_MB;
    pageBreak(headerH + Math.max(...rows[0].map((r) => r.h)) + outerPad * 2 + 10);
    const cardStartY = y;
    const cardStartPage = pageNum;
    pdf.setFillColor(...PD_SURFACE_2); pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
    pdf.rect(M, y, PW - M * 2, headerH, "F");
    pdf.line(M, y + headerH, PW - M, y + headerH);
    pdf.setTextColor(...PD_MUTED); pdf.setFontSize(10); pdf.setFont("helvetica", "bold");
    pdf.text("COORDINATION MEETING LOG", M + 9, y + 15);
    black(); pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
    y += headerH + outerPad;

    rows.forEach((row) => {
      const rowH = Math.max(...row.map((r) => r.h));
      pageBreak(rowH + gap);
      row.forEach((card, ci) => {
        const bx = M + outerPad + ci * (colW + gap);
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        pdf.roundedRect(bx, y, colW, rowH, 3, 3);

        let ty = y + mPad + 8;
        pdf.setFontSize(9); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        card.locationLines.forEach((l) => { pdf.text(l, bx + mPad, ty); ty += 10; });
        pdf.setFontSize(7.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INFO);
        pdf.text(fmtDt(card.m.meetingDate), bx + mPad, ty); ty += 12;

        pdf.setFontSize(8); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(card.condByLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        card.condByLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? card.condByLblW : 0), ty); ty += lineH; });
        ty += gapAfterCondBy;

        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(card.attLbl, bx + mPad, ty); ty += lineH;
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INK);
        card.attLines.forEach((l) => { pdf.text(l, bx + mPad, ty); ty += lineH; });
        ty += gapAfterAttendees;

        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
        pdf.line(bx + mPad, ty, bx + colW - mPad, ty);
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
        ty += dividerGap;

        pdf.setFont("helvetica", "bold"); pdf.setFontSize(8); pdf.setTextColor(...PD_INK);
        pdf.text(card.agendaLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        card.agendaLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? card.agendaLblW : 0), ty); ty += lineH; });

        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(card.minutesLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        card.minutesLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? card.minutesLblW : 0), ty); ty += lineH; });
      });
      y += rowH + gap;
    });
    black(); pdf.setFontSize(10); pdf.setFont("helvetica", "normal");

    /* Connected outer border wrapping the header + every meeting row as one
       card — same per-page-piece technique used for the permit type cards,
       so it stays a single visual card even if it spans a page break. */
    const cardEndY = y - gap + outerPad, cardEndPage = pageNum;
    const boxX = M, boxW = PW - M * 2;
    if (cardEndPage === cardStartPage) {
      pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
      pdf.roundedRect(boxX, cardStartY, boxW, cardEndY - cardStartY, 4, 4);
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    } else {
      const topY = 56, bottomY = PH - 58;
      for (let p = cardStartPage; p <= cardEndPage; p++) {
        pdf.setPage(p);
        pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.6);
        if (p === cardStartPage) {
          pdf.line(boxX, cardStartY, boxX + boxW, cardStartY);
          pdf.line(boxX, cardStartY, boxX, bottomY);
          pdf.line(boxX + boxW, cardStartY, boxX + boxW, bottomY);
        } else if (p === cardEndPage) {
          pdf.line(boxX, topY, boxX, cardEndY);
          pdf.line(boxX + boxW, topY, boxX + boxW, cardEndY);
          pdf.line(boxX, cardEndY, boxX + boxW, cardEndY);
        } else {
          pdf.line(boxX, topY, boxX, bottomY);
          pdf.line(boxX + boxW, topY, boxX + boxW, bottomY);
        }
        pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      }
      pdf.setPage(cardEndPage);
    }
    y += 14;
  }

  /* ════════════════ 11. GAS TESTS ════════════════ */
  const sortedGasTests = [...gasTests].sort((a, b) =>
    new Date(a.testedAt || 0) - new Date(b.testedAt || 0));
  if (sortedGasTests.length > 0) {
    const cols = 3, mPad = 8, lineH = 11;
    const colW = (PW - M * 2 - 20 - 10 * (cols - 1)) / cols;
    const innerW = colW - mPad * 2;
    const measured = sortedGasTests.map((g) => {
      const expired = g.validUntil && new Date(g.validUntil) < new Date();
      const readings = g.readings || [];
      pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold");
      const testedLbl = "Tested At: ", testedLblW = pdf.getTextWidth(testedLbl);
      const validLbl = "Valid Until: ", validLblW = pdf.getTextWidth(validLbl);
      const readingLines = readings.map((r) => {
        const label = `${r.parameter}: `;
        return { label, labelW: pdf.getTextWidth(label), value: str(r.reading) };
      });
      const remarkLines = g.remarks ? pdf.splitTextToSize(g.remarks, innerW) : [];
      const h = (mPad + 8) + 20
        + Math.max(readingLines.length, 1) * lineH + 8 + 14
        + lineH + lineH
        + (remarkLines.length > 0 ? remarkLines.length * lineH + 4 : 0)
        + mPad;
      return { g, expired, readingLines, testedLbl, testedLblW, validLbl, validLblW, remarkLines, h };
    });
    connectedCardSection("Gas Tests", cols, measured, (bx, topY, cw, item) => {
      const { g, expired, readingLines, testedLbl, testedLblW, validLbl, validLblW, remarkLines } = item;
      let ty = topY + mPad + 8;
      const category = g.category ? g.category.replace(/_/g, " ") : "PERIODIC";
      const catW = pill(category, PD_INFO, bx + mPad, ty, { fontSize: 7.5 });
      pill(expired ? "Expired" : "Valid", expired ? PD_CRIT : PD_OK, bx + mPad + catW + 8, ty, { fontSize: 7.5 });
      ty += 20;

      if (readingLines.length > 0) {
        readingLines.forEach(({ label, value }) => {
          pdf.setFontSize(8.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INK);
          pdf.text(label, bx + mPad, ty);
          pdf.setFont("helvetica", "bold");
          pdf.text(value, bx + mPad + pdf.getTextWidth(label), ty);
          ty += lineH;
        });
      } else {
        pdf.setFontSize(8.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        pdf.text("No readings recorded", bx + mPad, ty); ty += lineH;
      }
      ty += 8;

      pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
      pdf.line(bx + mPad, ty, bx + cw - mPad, ty);
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      ty += 14;

      pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(testedLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      pdf.text(fmtDt(g.testedAt), bx + mPad + testedLblW, ty);
      ty += lineH;

      pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(validLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      pdf.text(fmtDt(g.validUntil), bx + mPad + validLblW, ty);
      ty += lineH;

      if (remarkLines.length > 0) {
        ty += 4;
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        remarkLines.forEach((l) => { pdf.text(l, bx + mPad, ty); ty += lineH; });
      }
      black();
    });
  }

  /* ════════════════ 12. TOOL BOX TALKS (TBT) ════════════════ */
  const sortedTbt = [...toolBoxTalks].sort((a, b) =>
    new Date(a.conductedAt || a.createdAt || 0) - new Date(b.conductedAt || b.createdAt || 0));
  if (sortedTbt.length > 0) {
    const cols = 3, mPad = 8, lineH = 11;
    const colW = (PW - M * 2 - 20 - 10 * (cols - 1)) / cols;
    const innerW = colW - mPad * 2;
    const measured = sortedTbt.map((t) => {
      let attendeeStr = "-";
      if (Array.isArray(t.attendees) && t.attendees.length > 0) {
        attendeeStr = t.attendees.map(a =>
          a.employeeName ? a.employeeName : `${a.contractorName}${a.contractorAgency ? ` (${a.contractorAgency})` : ""} [Contractor]`
        ).join(", ");
      } else if (typeof t.attendees === "string" && t.attendees) {
        attendeeStr = t.attendees;
      }
      pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold");
      const condByLbl = "Conducted By: ", condByLblW = pdf.getTextWidth(condByLbl);
      const attLbl = "Attendees: ", attLblW = pdf.getTextWidth(attLbl);
      const notesLbl = "Notes: ", notesLblW = pdf.getTextWidth(notesLbl);
      const condByLines = pdf.splitTextToSize(t.conductedByName || t.conductedBy || "-", innerW - condByLblW);
      const attLines = pdf.splitTextToSize(attendeeStr, innerW - attLblW);
      const notesLines = pdf.splitTextToSize(t.notes || "-", innerW - notesLblW);
      const h = (mPad + 8) + 15
        + condByLines.length * lineH
        + attLines.length * lineH + 6 + 14
        + notesLines.length * lineH
        + mPad;
      return { t, condByLbl, condByLblW, attLbl, attLblW, notesLbl, notesLblW, condByLines, attLines, notesLines, h };
    });
    connectedCardSection("Tool Box Talks (TBT)", cols, measured, (bx, topY, cw, item) => {
      const { t, condByLbl, condByLblW, attLbl, attLblW, notesLbl, notesLblW, condByLines, attLines, notesLines } = item;
      let ty = topY + mPad + 8;
      pdf.setFontSize(10); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      const topicLines = pdf.splitTextToSize(str(t.topic || "-"), cw - mPad * 2 - 70);
      pdf.text(topicLines[0] || "-", bx + mPad, ty);
      pdf.setFontSize(8); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      const dtText = fmtDt(t.conductedAt || t.createdAt);
      pdf.text(dtText, bx + cw - mPad - pdf.getTextWidth(dtText), ty);
      ty += 15;

      pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(condByLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      condByLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? condByLblW : 0), ty); ty += lineH; });

      pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(attLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_INK);
      attLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? attLblW : 0), ty); ty += lineH; });
      ty += 6;

      pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
      pdf.line(bx + mPad, ty, bx + cw - mPad, ty);
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      ty += 14;

      pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(notesLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      notesLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? notesLblW : 0), ty); ty += lineH; });
      black();
    });
  }

  /* ════════════════ 13. JOB SAFETY ANALYSIS (JSA) ════════════════ */
  if (jsaList.length > 0) {
    section("Job Safety Analysis (JSA)");
    jsaList.forEach((jsa, idx) => {
      pageBreak(50);
      subHead(`#${idx + 1}  ${jsa.title || "-"}`);
      colorVal("Status", str(jsa.status), jsaColor(jsa.status), M + 8);
      row("Created By", str(jsa.createdByName || jsa.createdBy || "-"), "Created At", fmtDt(jsa.createdAt));
      if (jsa.workDescription) kv("Work Description", jsa.workDescription, M + 8);
      if (jsa.approverRemark) kv("Approver Remark", jsa.approverRemark, M + 8);
      if (jsa.steps?.length > 0) {
        y += 4; pdf.setFont("helvetica", "bold"); wl("Task Steps:", M + 8); pdf.setFont("helvetica", "normal");
        jsa.steps.forEach((step, si) => {
          pageBreak();
          wl(`${si + 1}. ${step.taskDescription || step.task || str(step)}`, M + 16);
          if (step.potentialHazards) wl(`   Hazards: ${step.potentialHazards}`, M + 16);
          if (step.controlMeasures) wl(`   Controls: ${step.controlMeasures}`, M + 16);
          if (step.responsiblePerson) wl(`   Responsible: ${step.responsiblePerson}`, M + 16);
          if (step.residualRisk) wl(`   Residual Risk: ${step.residualRisk}`, M + 16);
        });
      }
      y += 6;
    });
  }

  /* ════════════════ 14. SHIFT HANDOVERS ════════════════ */
  if (shiftHandovers.length > 0) {
    const cols = 3, mPad = 8, lineH = 11;
    const colW = (PW - M * 2 - 20 - 10 * (cols - 1)) / cols;
    const innerW = colW - mPad * 2;
    const shHandoverColor = (s) => s === "ACCEPTED" ? PD_OK : (s === "REJECTED" ? PD_CRIT : PD_WARN);
    const measured = shiftHandovers.map((ho) => {
      const status = ho.status ? ho.status.replace(/_/g, " ") : (ho.acceptedAt ? "ACCEPTED" : "PENDING");
      pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold");
      const outLbl = "Outgoing: ", outLblW = pdf.getTextWidth(outLbl);
      const inLbl = "Incoming: ", inLblW = pdf.getTextWidth(inLbl);
      const remarksLbl = "Remarks: ", remarksLblW = pdf.getTextWidth(remarksLbl);
      const acceptLbl = "Acceptance Remark: ", acceptLblW = pdf.getTextWidth(acceptLbl);
      const outLines = pdf.splitTextToSize(ho.outgoingUserName || "-", innerW - outLblW);
      const inLines = pdf.splitTextToSize(ho.incomingUserName || "-", innerW - inLblW);
      const remarksLines = pdf.splitTextToSize(ho.remarks || "-", innerW - remarksLblW);
      const acceptLines = ho.acceptedRemark ? pdf.splitTextToSize(ho.acceptedRemark, innerW - acceptLblW) : [];
      const h = (mPad + 8) + 15
        + outLines.length * lineH
        + inLines.length * lineH + 6 + 14
        + remarksLines.length * lineH
        + (acceptLines.length > 0 ? acceptLines.length * lineH : 0)
        + mPad;
      return { ho, status, outLbl, outLblW, inLbl, inLblW, remarksLbl, remarksLblW, acceptLbl, acceptLblW, outLines, inLines, remarksLines, acceptLines, h, accentColor: shHandoverColor(status) };
    });
    connectedCardSection("Shift Handovers", cols, measured, (bx, topY, cw, item) => {
      const { ho, status, outLbl, outLblW, inLbl, inLblW, remarksLbl, remarksLblW, acceptLbl, acceptLblW, outLines, inLines, remarksLines, acceptLines } = item;
      let ty = topY + mPad + 8;
      pdf.setFontSize(8.5); pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      pdf.text(fmtDt(ho.handoverTime), bx + mPad, ty);
      pill(status, item.accentColor, bx + cw - mPad - (pdf.getTextWidth(status) + 20), ty, { fontSize: 7.5 });
      ty += 20;

      pdf.setFontSize(8.5); pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(outLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      outLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? outLblW : 0), ty); ty += lineH; });

      pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(inLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      inLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? inLblW : 0), ty); ty += lineH; });
      ty += 6;

      pdf.setDrawColor(...PD_LINE); pdf.setLineWidth(0.5);
      pdf.line(bx + mPad, ty, bx + cw - mPad, ty);
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
      ty += 14;

      pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
      pdf.text(remarksLbl, bx + mPad, ty);
      pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
      remarksLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? remarksLblW : 0), ty); ty += lineH; });

      if (acceptLines.length > 0) {
        pdf.setFont("helvetica", "bold"); pdf.setTextColor(...PD_INK);
        pdf.text(acceptLbl, bx + mPad, ty);
        pdf.setFont("helvetica", "normal"); pdf.setTextColor(...PD_MUTED);
        acceptLines.forEach((l, li) => { pdf.text(l, bx + mPad + (li === 0 ? acceptLblW : 0), ty); ty += lineH; });
      }
      black();
    });
  }


  /* ════════════════ FOOTER ════════════════ */
  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    pdf.setFontSize(8); black();
    pdf.setDrawColor(180, 180, 180); pdf.setLineWidth(0.4);
    pdf.line(M, PH - 30, PW - M, PH - 30);
    pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.2);
    pdf.text(`Permit #${permit?.id || "—"}  |  ${formatPermitTrackingId(permit)}  |  Page ${i} of ${total}`, M, PH - 18);
    pdf.text(`Generated: ${fmtNow(new Date())}`, PW - M, PH - 18, { align: "right" });
  }

  return pdf;
}


async function fetchJson(url) {
  const res = await fetch(url, { method: "GET", mode: "cors", headers: headers() });
  return res.json().catch(() => null);
}

export async function createPermitPdf(permitId) {
  const [fullRes, checklistsRes, revalidations, isolationRecords, jsaList, simopsConflicts, coordinationMeetings, gasTests, toolBoxTalks, shiftHandovers] =
    await Promise.all([
      fetchJson(`${restApiUrl}/api/permits/${permitId}/full`),
      fetchJson(`${restApiUrl}/api/checklists?page=0&size=200&sort=name,asc`),
      fetchJson(`${restApiUrl}/api/permit-revalidations/permit/${permitId}`),
      fetchJson(`${restApiUrl}/api/isolation-records/permit/${permitId}`),
      fetchJson(`${restApiUrl}/api/jsa/permit/${permitId}`),
      fetchJson(`${restApiUrl}/api/permits/${permitId}/simops`),
      fetchJson(`${restApiUrl}/api/permits/${permitId}/coordination-meetings`),
      fetchJson(`${restApiUrl}/api/gas-tests/permit/${permitId}`),
      fetchJson(`${restApiUrl}/api/tool-box-talks/permit/${permitId}`),
      fetchJson(`${restApiUrl}/api/shift-handovers/permit/${permitId}`),
    ]);

  const full = fullRes && fullRes.success ? fullRes.data : null;
  const permit = full?.permit ? { ...full.permit, id: permitId } : { id: permitId };
  const blocks = Array.isArray(permit.permitTypeBlocks)
    ? permit.permitTypeBlocks
    : full?.permitTypeBlocks || [];

  // Top-level checklistItems (from /full) carry saved fieldsValues; master
  // checklists carry the field label/type definitions. Merge both into
  // each block's checklistItems so the PDF can render field values.
  const fieldValuesById = {};
  (full?.checklistItems || []).forEach((ci) => {
    if (ci.id != null) fieldValuesById[ci.id] = ci.fieldsValues || {};
  });
  const masterChecklists = checklistsRes?.data?.content || checklistsRes?.data || [];
  const fieldsByChecklistId = {};
  masterChecklists.forEach((mc) => {
    if (mc.id != null) fieldsByChecklistId[mc.id] = mc.fields || [];
  });
  const enhancedBlocks = blocks.map((b) => ({
    ...b,
    checklistItems: (b.checklistItems || []).map((ci) => ({
      ...ci,
      fieldsValues: ci.fieldsValues || fieldValuesById[ci.id] || {},
      fields: ci.fields || fieldsByChecklistId[ci.permitTypeChecklistId] || [],
    })),
  }));

  return buildPermitPdf(permit, {
    permitTypeBlocks: enhancedBlocks,
    riskItems: full?.riskAssessmentItems || permit.riskAssessmentItems || [],
    riskAssessment: full?.riskAssessment || permit.riskAssessment || null,
    blanketCheckins: permit.blanketCheckins || [],
    revalidations: revalidations && revalidations.success ? revalidations.data || [] : [],
    isolationRecords: isolationRecords && isolationRecords.success ? isolationRecords.data || [] : [],
    jsaList: jsaList && jsaList.success ? jsaList.data || [] : [],
    simopsConflicts: Array.isArray(simopsConflicts) ? simopsConflicts : [],
    coordinationMeetings: Array.isArray(coordinationMeetings) ? coordinationMeetings : [],
    gasTests: gasTests && gasTests.success ? gasTests.data || [] : [],
    toolBoxTalks: toolBoxTalks && toolBoxTalks.success ? toolBoxTalks.data || [] : [],
    shiftHandovers: shiftHandovers && shiftHandovers.success ? shiftHandovers.data || [] : [],
  });
}

export default function PermitPdf({
  permit,
  asIcon = false,
  disabled = false,
  sx = {},
  title,
}) {
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const pdf = await createPermitPdf(permit?.id);
      if (pdf?.save) pdf.save(`permit-${permit?.id || "export"}.pdf`);
      else console.error("PDF object invalid", pdf);
    } catch (err) {
      console.error("Error creating PDF:", err);
    } finally {
      setExporting(false);
    }
  };

  if (asIcon) {
    return (
      <IconButton size="small" onClick={handleExport} disabled={disabled || exporting}
        sx={Object.assign({ width: 40, height: 40 }, sx)} aria-label="export-pdf" title={title}>
        <PictureAsPdfIcon fontSize="small" />
      </IconButton>
    );
  }

  return (
    <button onClick={handleExport} disabled={disabled || exporting} title={title}
      className={`px-4 py-2 rounded ${disabled || exporting ? "bg-gray-300 text-gray-600" : "bg-green-600 text-white"}`}>
      {exporting ? "Exporting..." : "Export PDF"}
    </button>
  );
}
